import React, { useCallback, useMemo, useState } from 'react';
import { isMobileOnly } from 'react-device-detect';

import {
  Box,
  IconButton,
  Stack,
  Typography,
  type SxProps,
} from '@mui/material';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import MenuIcon from '@mui/icons-material/Menu';
import AppLogo from '@components/AppLogo';
import DashboardBarButton from './DashboardBarButton';
import ParticipantsButton from './ParticipantsButton';
import FullScreenButton from './FullScreenButton';
import UserAvatarButton from '@components/UserAvatarButton';
import AdminTools from './AdminTools';
import { ReactComponent as LogoutIcon } from '@public/images/account_exit.svg';
import { env } from 'env';
import MytaverseAppBar, {
  AppBarPositionEnum,
} from '@components/MytaverseAppBar';

import { EventDrawerTabs } from '../../../constants';

import { IDashboardTopBarContentView } from './interfaces';

import { useStyles } from './styles';

import { useMytaverseEvent } from '@core/dashboard/providers';
import ConfirmModal from '@components/ConfirmModal';
import ConfirmLogoutModal, {
  useOpenConfirmLogoutModal,
} from '@components/ConfirmLogoutModal';
import { useChatState } from '@core/chat';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { useMytaverse } from '@providers/MytaverseProvider';
import ParticipantScopes from '@constants/scopes';

import { getSDEvent } from '@clients/ek/helpers';
import ROUTES from '@constants/routes';

const {
  REACT_APP_SHOW_LEFT_BAR,
  REACT_APP_SHOW_PROFILE,
  REACT_APP_SHOW_ADMIN_TOOLS,
  REACT_APP_EXTERNAL_EVENT_TAG,
} = env;

const DashboardTopBarContentView = ({
  currentRoom,
  isOpenChat,
  hasMediaStream,
  participantTotal,
  onOpenEventDrawer,
  currentEvent,
}: IDashboardTopBarContentView) => {
  const classes = useStyles({
    isOpenChat,
    hasMediaStream,
  });
  const { pathname, search } = useLocation();
  const { t } = useTranslation();
  const { skyLinkExperience, events, cleanStates } = useMytaverseEvent();
  const { resetState } = useChatState();
  const [showExitSkyLinkModal, setShowExitSkyLinkModal] = useState(false);
  const {
    open: isOpenedConfirmLogoutModal,
    openModal: onOpenConfirmLogoutModal,
    closeModal: onCloseConfirmLogoutModal,
  } = useOpenConfirmLogoutModal();
  const { userPermissions } = useMytaverse();
  const showLeftMenu = currentEvent?.enablePureGamecast || currentRoom;
  const showAdminTools = REACT_APP_SHOW_ADMIN_TOOLS === 'true';
  const showLeftBar = REACT_APP_SHOW_LEFT_BAR === 'true';
  const showProfile =
    REACT_APP_SHOW_PROFILE === 'true' ||
    (REACT_APP_EXTERNAL_EVENT_TAG
      ? currentEvent?.eventTags?.includes(REACT_APP_EXTERNAL_EVENT_TAG)
      : false);

  //TODO: when it's active, it should be added to mobile as well
  const isAdminToolEnabled =
    showAdminTools &&
    userPermissions?.includes(ParticipantScopes.HubAdminTools);
  const allowOnMobileForExternalEvent =
    !!REACT_APP_EXTERNAL_EVENT_TAG &&
    currentEvent?.eventTags?.includes(REACT_APP_EXTERNAL_EVENT_TAG);
  const isSkylinkTrue = useMemo(() => {
    const params = new URLSearchParams(search);
    return params.get('skylink') === 'true';
  }, [search]);

  const handleExitButtonClick = () => {
    const targetEvent = getSDEvent(events);

    if (targetEvent) {
      localStorage.setItem('returnToSkyLinkPortal', 'true');

      window.location.assign(ROUTES.EVENT_PAGE(targetEvent.eventId));
    } else {
      console.log(
        'No matching event found with SD, FullTimeSDHub tags (excluding LINUX)',
      );
    }
  };

  const handleOnConfirmLogout = useCallback(() => {
    cleanStates();
    resetState();
  }, [cleanStates, resetState]);

  if (isMobileOnly && !allowOnMobileForExternalEvent) {
    return null;
  }

  const renderTopRightActions = (customSx?: SxProps) => (
    <MytaverseAppBar
      position={AppBarPositionEnum.TopRight}
      isCollapsible={false}
      sx={customSx}
    >
      <FullScreenButton />
      {isAdminToolEnabled ? <AdminTools /> : null}
      {showProfile ? <UserAvatarButton /> : null}
    </MytaverseAppBar>
  );

  return (
    <>
      {showLeftBar ? (
        <MytaverseAppBar
          position={AppBarPositionEnum.TopLeft}
          sx={classes.leftBar}
          isCollapsible={false}
          isGroup={false}
        >
          {showLeftMenu ? (
            <>
              <Box sx={classes.root} component="div">
                <DashboardBarButton
                  buttonIcon={<LocationOnIcon />}
                  onClick={() => onOpenEventDrawer(EventDrawerTabs.Locations)}
                >
                  {currentRoom?.name || 'Lobby'}
                </DashboardBarButton>
                <ParticipantsButton
                  onClick={() =>
                    onOpenEventDrawer(EventDrawerTabs.Participants)
                  }
                  total={participantTotal}
                />
              </Box>
              <IconButton
                sx={classes.iconButton}
                onClick={() => onOpenEventDrawer(EventDrawerTabs.Locations)}
              >
                <MenuIcon />
              </IconButton>
            </>
          ) : (
            <Box sx={classes.rootLoading}>
              <Stack flexDirection="row" alignItems="center">
                <AppLogo />
              </Stack>
            </Box>
          )}
        </MytaverseAppBar>
      ) : null}
      {skyLinkExperience ? (
        <Box sx={classes.skyLinkTopRightWrapper}>
          <MytaverseAppBar
            position={AppBarPositionEnum.TopRight}
            isCollapsible={false}
            sx={classes.skyLinkInlineAppBar}
          >
            <Box
              sx={classes.skyLinkReturnContainer}
              onClick={
                isSkylinkTrue
                  ? () => setShowExitSkyLinkModal(true)
                  : onOpenConfirmLogoutModal
              }
            >
              <LogoutIcon />
              <Typography sx={classes.skyLinkReturnLabel}>
                {isSkylinkTrue
                  ? t('skyLink.returnToHub')
                  : t('common:logoutConfirm.exit')}
              </Typography>
            </Box>
          </MytaverseAppBar>
          {renderTopRightActions(classes.skyLinkInlineAppBar)}
        </Box>
      ) : (
        renderTopRightActions()
      )}

      {skyLinkExperience ? (
        isSkylinkTrue ? (
          <ConfirmModal
            open={showExitSkyLinkModal}
            title={t('skyLink.exitConfirmTitle')}
            message={t('skyLink.exitConfirmMessage')}
            onConfirm={handleExitButtonClick}
            onCancel={() => setShowExitSkyLinkModal(false)}
            cancelButtonName={t('common:logoutConfirm.stayHub')}
            confirmButtonName={t('common:logoutConfirm.exitHub')}
            altStyling
          />
        ) : (
          <ConfirmLogoutModal
            open={isOpenedConfirmLogoutModal}
            onClose={onCloseConfirmLogoutModal}
            onConfirm={handleOnConfirmLogout}
            altStyling
          />
        )
      ) : null}
    </>
  );
};

export default DashboardTopBarContentView;
